import {
  Flex,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Select,
  Text,
  Box,
} from '@chakra-ui/react';
import { useSendAdditionalDoneProjects } from '~/common/api/orderingServiceApi/OrderingApi';
import {
  OrderedProjectDurationSummary,
  ProjectWithAmount,
} from '@its4plan/ordering';
import { Project } from '~/common/entities/Project/ProjectTypes';
import { DatePickerInput } from 'chakra-datetime-picker';
import useTranslate from '~/common/utils/translate';
import AdjustAmountRow from './AdjustAmountRow';
import { BiInfoCircle } from 'react-icons/bi';
import { useState } from 'react';

interface AdjustFinishedProjectsModalProps {
  isModalOpen: boolean;
  onClose: () => void;
  allProjects: Project[];
  projectsDurationSummaries: OrderedProjectDurationSummary[];
}

const AdjustFinishedProjectsModal: React.FC<
  AdjustFinishedProjectsModalProps
> = ({ isModalOpen, onClose, allProjects, projectsDurationSummaries }) => {
  const [selectedProjectId, setSelectedProjectId] = useState<number | ''>('');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);
  const [adjustedAmounts, setAdjustedAmounts] = useState<ProjectWithAmount[]>(
    [],
  );

  const sendAdditionalDoneProjects = useSendAdditionalDoneProjects();

  const translate = useTranslate();

  const onSubmit = (
    adjustedAmounts: ProjectWithAmount[],
    selectedDate: Date,
  ) => {
    onClose();
    sendAdditionalDoneProjects.mutate({
      doneDate: selectedDate,
      projectsWithAmounts: adjustedAmounts,
    });
  };

  const selectedProject = allProjects.find((p) => p.id === selectedProjectId);

  const getAccumulatedAmount = (name: string): number =>
    projectsDurationSummaries.find((ps) => ps.projectName === name)
      ?.doneAmount || 0;

  const handleAmountChange = (name: string, newAmount: number) => {
    setAdjustedAmounts((prev) => {
      const updated = prev.filter((entry) => entry.projectName !== name);
      updated.push({ projectName: name, amount: newAmount });
      return updated;
    });
  };

  const handleSubmit = () => {
    if (!selectedDate) {
      setShowInfoTooltip(true);
      setTimeout(() => setShowInfoTooltip(false), 4000);
      return;
    }
    onSubmit(adjustedAmounts, selectedDate);
    setAdjustedAmounts([]);
    setSelectedProjectId(-1);
    setSelectedDate(null);
    onModalClose();
  };

  const onModalClose = () => {
    onClose();
    setSelectedProjectId('');
  };

  return (
    <Modal isOpen={isModalOpen} onClose={onModalClose}>
      <ModalOverlay />
      <ModalContent maxW="fit-content" w="auto">
        <ModalHeader>
          {translate('analysis.company.summary.showDoneOrders.Button')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex direction="row" gap={4} mb={4}>
            <Select
              minW="300px"
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(Number(e.target.value))}
            >
              <option value="" disabled hidden>
                {translate('analysis.modal.select.project.label')}
              </option>
              {allProjects
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                .filter((project) => project.is_option === false)
                .map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
            </Select>
            <DatePickerInput
              value={selectedDate ? selectedDate.toDateString() : ''}
              onChange={(dateString: string) =>
                setSelectedDate(dateString ? new Date(dateString) : null)
              }
            />
            <Tooltip
              label={translate('analysis.modal.select.date.info')}
              isOpen={showInfoTooltip}
              onClose={() => setShowInfoTooltip(false)}
            >
              <span
                onMouseEnter={() => setShowInfoTooltip(true)}
                onMouseLeave={() => setShowInfoTooltip(false)}
              >
                <BiInfoCircle
                  size={20}
                  color="green"
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                  }}
                />
              </span>
            </Tooltip>
          </Flex>

          {selectedProject && (
            <>
              <Flex direction="row" gap={4} align="center">
                <Text width="40%" fontWeight="bold">
                  {translate('adjust.amount.row.name.label')}
                </Text>
                <Text width="30%" fontWeight="bold">
                  {translate('adjust.amount.row.amount.label')}
                </Text>
                <Text width="30%" fontWeight="bold">
                  {translate('adjust.amount.row.correction.amount.label')}
                </Text>{' '}
              </Flex>
              <Box mt={3} mb={3} key={selectedProject.id}>
                <AdjustAmountRow
                  name={selectedProject.name}
                  accumulatedAmount={getAccumulatedAmount(selectedProject.name)}
                  onAdditionalAmountChangeParent={handleAmountChange}
                />
              </Box>
              {selectedProject.option_ids?.map((optionId) => {
                const option = allProjects.find(
                  (project) => project.id === optionId,
                );
                if (!option) return null;
                return (
                  <Box mb={3} key={option.id}>
                    <AdjustAmountRow
                      name={option.name}
                      accumulatedAmount={getAccumulatedAmount(option.name)}
                      onAdditionalAmountChangeParent={handleAmountChange}
                    />
                  </Box>
                );
              })}
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" onClick={onModalClose}>
            {translate('analysis.modal.cancel')}
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isDisabled={!selectedProjectId}
          >
            {translate('analysis.modal.submit')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AdjustFinishedProjectsModal;
